function debounceFunc(func, wait) {
  let timeout;
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

class QuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.querySelectorAll("button").forEach((button) => button.addEventListener("click", this.onButtonClick.bind(this)));
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === "plus" ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) {
      if(!this.closest('cart-products')) return;
      CartProducts.triggerChange(this.closest('cart-products'), this.input);
    }
  }
}

customElements.define("quantity-selector", QuantitySelector);


class RemoveItemButtom extends HTMLElement {
  constructor() {
    super();
  }
}

// Document-level capture-phase delegation. This is the most reliable way to
// intercept the trash-icon tap on iOS Safari:
// - capture phase fires BEFORE the inner <a href="..."> can start navigation
// - delegation means we don't depend on custom-element upgrade timing
// - works even on older iOS Safari versions
document.addEventListener(
  "click",
  function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var removeBtn = target.closest("remove-item-button");
    if (!removeBtn) return;

    event.preventDefault();
    event.stopPropagation();

    var cartProducts = removeBtn.closest("cart-products");
    if (cartProducts && typeof cartProducts.updateQuantity === "function") {
      cartProducts.updateQuantity(removeBtn.dataset.index, 0);
    }
  },
  true
);

customElements.define("remove-item-button", RemoveItemButtom);


class CartProducts extends HTMLElement {
  constructor () {
    super();
    this.updateShipsBy();
    this.removeBundleProductActions();
  }

  static triggerChange(instance, input) {
    const debouncedOnChange = debounceFunc(instance.onChange.bind(instance), 300);
    debouncedOnChange(input);
  }

  onChange(input) {
    this.updateQuantity(input.dataset.index, input.value)
  }

  // Returns the line keys/numbers of Extend protection plans tied to the removed
  // line item. Extend warranties are independent line items (NOT linked through
  // data-coverage-ref) that reference their covered product's VARIANT id under
  // one of several property keys depending on how the plan was added:
  //   - "Ref"              -> PDP product offer
  //   - "_Extend.ProductId" -> cart / price-point offer
  //   - "_Extend.BundleId"  -> bundle offer
  // This mirrors Extend's own warrantyAlreadyInCart matching.
  getExtendWarrantyLines(lineItemEl, quantity) {
    const matches = [];
    if (!lineItemEl) return matches;

    const readData = (el) => {
      const tag = el && el.querySelector('script[type="application/json"]');
      if (!tag) return null;
      try { return JSON.parse(tag.textContent); } catch (e) { return null; }
    };

    const removed = readData(lineItemEl);
    const removedVariantId = removed ? String(removed.variant_id) : null;
    if (!removedVariantId) return matches;

    document.querySelectorAll(".cart-products__item").forEach((cartItem) => {
      if (cartItem === lineItemEl) return;
      const data = readData(cartItem);
      const props = data && data.properties;
      // Only ever remove genuine Extend protection plans.
      if (!props || !props["__Extend.IsExtendWarranty"]) return;
      const refs = [props.Ref, props["_Extend.ProductId"], props["_Extend.BundleId"]]
        .filter(Boolean)
        .map(String);
      if (refs.includes(removedVariantId)) {
        matches.push({ key: cartItem.dataset.line, lineNumber: cartItem.dataset.lineNumber });
      }
    });

    return matches;
  }

  updateQuantity(line, quantity) {
    this.setLoading(line);

    const lineItemEl = document.getElementById(`cart-item-${line}`);

    // Extend protection plans are added to the cart as independent line items.
    // Collect any tied to this product so they track its quantity (and removal).
    const extendWarrantyLines = this.getExtendWarrantyLines(lineItemEl, quantity);

    const mergeExtendWarranties = (updates, removedItems) => {
      extendWarrantyLines.forEach(({ key, lineNumber }) => {
        if (!(key in updates)) {
          updates[key] = quantity;
          removedItems.push({ line: lineNumber, quantity });
        }
      });
    };

    // Remove bundle items
    if (lineItemEl?.querySelector(".property.is-bundle")) {
      const bundleId = lineItemEl.querySelector(".property.is-bundle .property-value").innerHTML;
      const cartItems = document.querySelectorAll(".cart-products__item");

      const removedItems = [];
      const updates = {};
      cartItems.forEach((cartItem) => {
        if (cartItem.querySelector(".property.is-bundle") && bundleId === cartItem.querySelector(".property.is-bundle .property-value").innerHTML) {
          updates[cartItem.dataset.line] = quantity;
          removedItems.push({ line: cartItem.dataset.lineNumber, quantity: quantity });
        }
      });

      mergeExtendWarranties(updates, removedItems);
      this.bulkUpdate(updates, removedItems, line, quantity);
    } else if (lineItemEl?.hasAttribute('data-coverage-ref') || lineItemEl?.hasAttribute('data-addons-ref') || extendWarrantyLines.length > 0) {
      const cartItems = document.querySelectorAll(".cart-products__item");

      const removedItems = [];
      const updates = {};

      const coverageRef = lineItemEl.dataset.coverageRef;
      const addonsRef = lineItemEl.dataset.addonsRef;

      // Compare property values as integers — addonsRef / coverageRef are
      // variant IDs. innerHTML can include extra whitespace or be serialised
      // differently across browsers; textContent + parseInt is robust.
      const refMatches = (cartItem, selector, ref) => {
        if (!ref) return false;
        const el = cartItem.querySelector(selector);
        if (!el) return false;
        const value = (el.textContent || el.innerHTML || '').trim();
        return value === ref || parseInt(value, 10) === parseInt(ref, 10);
      };

      cartItems.forEach((cartItem) => {
        // sempre atualiza o próprio produto
        if (cartItem === lineItemEl) {
          updates[cartItem.dataset.line] = quantity;
          removedItems.push({ line: cartItem.dataset.lineNumber, quantity });
          return;
        }
        // checa se este item é um "coverage" vinculado
        if (refMatches(cartItem, "[data-coverage] .property-value", coverageRef)) {
          updates[cartItem.dataset.line] = quantity;
          removedItems.push({ line: cartItem.dataset.lineNumber, quantity });
          return;
        }
        // checa se este item é um "addon" vinculado
        if (refMatches(cartItem, "[data-addon] .property-value", addonsRef)) {
          updates[cartItem.dataset.line] = quantity;
          removedItems.push({ line: cartItem.dataset.lineNumber, quantity });
        }
      });

      mergeExtendWarranties(updates, removedItems);
      this.bulkUpdate(updates, removedItems, line, quantity);
    } else {
      const cartSectionId = document.getElementById("cart").dataset.id;
      const body = JSON.stringify({
        line,
        quantity,
        sections: cartSectionId,
      });

      fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        document.querySelector('.cart-block').classList.toggle("empty", parsedState.item_count === 0);

        this.sendRemovedItemToAnalytics(line, quantity);
        this.updateElements(line, quantity, parsedState.sections[cartSectionId]);
      })
      .catch((err) => {
        this.setLoading(line);
        console.log(err);
      });
    }
  }

  // Bulk-update multiple cart line items (e.g. parent + linked addons / bundle)
  // and patch the cart section in place — same UX as single-item removal,
  // no full page reload.
  bulkUpdate(updates, removedItems, line, quantity) {
    const cartSectionEl = document.getElementById("cart");
    const cartSectionId = cartSectionEl?.dataset.id;

    fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        updates,
        sections: cartSectionId,
      }),
    })
      .then((response) => response.json())
      .then((parsedState) => {
        document.querySelector('.cart-block')?.classList.toggle("empty", parsedState.item_count === 0);

        // Fire analytics BEFORE patching the DOM (the analytics handler looks
        // up the soon-to-be-removed cart-item-* elements to read their data).
        if (removedItems?.length > 0) {
          removedItems.forEach((item) => {
            this.sendRemovedItemToAnalytics(item.line, item.quantity);
          });
        }

        this.updateElements(line, quantity, parsedState.sections?.[cartSectionId]);
      })
      .catch((err) => {
        this.setLoading(line);
        console.log(err);
      });
  }

  sendRemovedItemToAnalytics(line, quantity) {
    if(quantity === 0 || parseInt(quantity) === 0) {
      const targetItem = document.getElementById(`cart-item-${line}`);

      if(!targetItem) return;

      const itemData = JSON.parse(targetItem.querySelector('[type="application/json"]').textContent);

      const item_removed_event = new CustomEvent('cart_item_removed', {
        detail: {
          value: itemData.final_line_price,
          item: {
            item_id: itemData.variant_id,
            item_name: itemData.product_title,
            item_category: itemData.product_type,
            price: itemData.price,
            quantity: itemData.quantity
          }
        }
      });

      document.dispatchEvent(item_removed_event);
    }
  }

  updateElements(line, quantity, parsedSection) {
    const section = document.getElementById('cart');
    if(section && parsedSection) {
      if(parseInt(quantity) > 0) {
        const elementsToUpdate = [`cart-items`, "cart-summary", "one-percent"];
        if(elementsToUpdate?.length > 0) {
          elementsToUpdate.forEach(element => {
            const targetElement = document.getElementById(element);
            if(targetElement) {
              targetElement.innerHTML = this.getSectionInnerHTML(parsedSection, element);
            }
          });
        }
      } else {
        const cartItems = document.getElementById('cart-items');
        const cartSummary = document.getElementById('cart-summary');
        const cartHeading = document.querySelector('.cart-products__heading h4');
        const headerBagCounter = document.querySelector('.header__link.cart__link .cart__counter');
        if(cartItems) {
          cartItems.innerHTML = this.getSectionInnerHTML(parsedSection, "cart-items");
        }
        if(cartSummary) {
          cartSummary.innerHTML = this.getSectionInnerHTML(parsedSection, "cart-summary");
        }
        if(cartHeading) {
          const numberOfItems = cartItems.querySelectorAll('.cart-products__item');
          if(numberOfItems?.length > 0) {
            cartHeading.innerHTML = `Your cart (${numberOfItems.length})`;
            headerBagCounter.innerHTML = numberOfItems.length;
          } else {
            headerBagCounter.innerHTML = 0;
            headerBagCounter.parentNode.querySelector('svg').querySelector('path').setAttribute('fill', '#252525');
          }
        }
      }
    }
  }

  async updateShipsBy() {
    function addBusinessDays(startDate, daysToAdd) {
      let currentDate = new Date(startDate.getTime());
      while (daysToAdd > 0) {
        currentDate.setDate(currentDate.getDate() + 1);
        if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
          daysToAdd--;
        }
      }
      return currentDate;
    }

    function getOrdinalSuffix(number) {
      if (number >= 11 && number <= 13) {
        return "th";
      }
      switch (number % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    }

    try {
      const fetchCart = await fetch(window.Shopify.routes.root + "cart.js", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!fetchCart.ok) {
        throw new Error(`Failed to fetch cart data. Status: ${fetchCart.status}`);
      }

      const cartData = await fetchCart.json();
      if (cartData.items?.length > 0) {
        const cartItems = cartData.items;
        for (let item of cartItems) {
          if (item.properties?.hasOwnProperty("Ships by") && item.properties?.hasOwnProperty("_ships_by_date")) {
            const itemEl = document.querySelector(`[data-line="${item.key}"]`);
            const shipsByEl = itemEl.querySelector(".property.ships-by .property-value");

            let leadTime = itemEl.dataset.variantLeadTime;
            let leadTimeDate = itemEl.dataset.variantLeadTimeDate;
            if (leadTimeDate) {
              let time;
              let timezoneOffset;
              leadTimeDate = new Date(leadTimeDate);

              time = leadTimeDate.getTime();
              timezoneOffset = leadTimeDate.getTimezoneOffset() * 60000;
              leadTimeDate = new Date(time + timezoneOffset);
            }
            let leadTimeDays = itemEl.dataset.variantLeadDays;
            if (leadTimeDays) {
              leadTimeDays = parseInt(leadTimeDays);
            }

            const Property1Date = new Date(item.properties["_ships_by_date"]);
            const newBusinessDate = addBusinessDays(new Date(), leadTimeDays + 1);

            if (newBusinessDate.getTime() > Property1Date.getTime()) {
              let body = {
                id: item.key,
                properties: {},
              };

              if (leadTime && leadTimeDate) {
                body.properties["Ships by"] = leadTime;
                body.properties["_ships_by_date"] = leadTimeDate.toLocaleDateString("en-US", { year: "numeric", month: "numeric", day: "numeric" });
              } else if (!leadTime && leadTimeDate) {
                body.properties["Ships by"] = `Ships by ${leadTimeDate.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + getOrdinalSuffix(leadTimeDate.getDate())}`;
                body.properties["_ships_by_date"] = leadTimeDate.toLocaleDateString("en-US", { year: "numeric", month: "numeric", day: "numeric" });
              } else if (!leadTime && leadTimeDays && leadTimeDays > 0) {
                body.properties["Ships by"] = `Ships by ${newBusinessDate.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + getOrdinalSuffix(businessDate.getDate())}`;
                body.properties["_ships_by_date"] = newBusinessDate.toLocaleDateString("en-US", { year: "numeric", month: "numeric", day: "numeric" });
              } else if (leadTime && leadTimeDays && leadTimeDays > 0) {
                body.properties["Ships by"] = leadTime;
                body.properties["_ships_by_date"] = newBusinessDate.toLocaleDateString("en-US", { year: "numeric", month: "numeric", day: "numeric" });
              } else {
                body.properties["_ships_by_date"] = "not-set";
              }

              try {
                fetch(window.Shopify.routes.root + "cart/change.js", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify(body)
                })
                .then(() => {
                  if (shipsByEl && body.properties.hasOwnProperty("Ships by")) {
                    shipsByEl.innerHTML = `${body.properties["Ships by"]}`;
                  }
                })
                .catch((err) => console.log(err));
              } catch (err) {
                console.log(`Failed to change cart data.`, err);
              }
            }
          }
        }
      }
    } catch (err) {
      console.error("Error fetching cart data:", err);
    }
  }

  removeBundleProductActions() {
    const bundleItems = document.querySelectorAll('.cart-products__item.has-bundle');
    const firstItemOfEachBundle = {};
    const nonFirstBundleItems = [];

    for (let i = 0; i < bundleItems.length; i++) {
      let item = bundleItems[i];
      let itemBundleId = item.querySelector('.property.is-bundle .property-value').innerHTML.trim();

      if (!firstItemOfEachBundle[itemBundleId]) {
        firstItemOfEachBundle[itemBundleId] = item;
        continue;
      }

      nonFirstBundleItems.push(item);
    }

    nonFirstBundleItems.forEach(item => {
      item.querySelector('.item__actions').classList.add('hidden');
    });
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, "text/html").getElementById(selector)?.innerHTML;
  }

  setLoading(line) {
    const priceEl = document.getElementById(`cart-item-${line}`).querySelector('.item__final-price');
    if(priceEl) {
      priceEl.classList.toggle('loading');
    }
  }
}

if(!customElements.get('cart-products')) {
  customElements.define('cart-products', CartProducts);
}